import { Star } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// Real Google Reviews from Gomez Cleaning Service
const googleReviews = [
  {
    author_name: 'Paula Kleiman',
    rating: 5,
    relative_time_description: '2 years ago',
    text: 'Gomez Cleaning Service did an amazing job on our house. They cleaned very thoroughly and left our house beautiful. We will be using her services weekly from now on. Thank you.',
  },
  {
    author_name: 'KateSynnottMakeUp',
    rating: 5,
    relative_time_description: '2 years ago',
    text: "Elvia is incredible. Her attention to detail why cleaning is next level. We get the deep clean every few weeks it's incredible! Especially with kiddos in the house.",
  },
  {
    author_name: 'Carrie Downs-Minetree',
    rating: 5,
    relative_time_description: '2 years ago',
    text: "The crew of Gomez Cleaning Service is fantastic. I've lived in my house for over 30 years and it definitely needs some TLC. They did an amazing job!",
  },
  {
    author_name: 'Lois McGregor',
    rating: 5,
    relative_time_description: 'a year ago',
    text: 'I highly recommend Gómez cleaning service. They were very pleasant, professional and qualified. Elvia and the crew were kind and accommodating with any special needs. The house looked amazing and super clean! I will hire them again.',
  },
  {
    author_name: 'Shannon Muscat',
    rating: 5,
    relative_time_description: '11 months ago',
    text: "Always does a GREAT job! Even my husband is impressed (that's rare)! VERY TRUSTWORTHY!",
  },
  {
    author_name: 'Martina Puentes',
    rating: 5,
    relative_time_description: 'a year ago',
    text: "I'm very happy and satisfied with Gomez Cleaning Service. Very honest and I feel very comfortable having them in my home.",
  },
  {
    author_name: 'Paula Reyes',
    rating: 5,
    relative_time_description: 'a year ago',
    text: 'Excellent equipment as they work, impeccable cleaning, they remove stains that others have not been able to do. Thank you.',
  },
  {
    author_name: 'Rigo Mendoza',
    rating: 5,
    relative_time_description: 'a year ago',
    text: 'Great experience. Super professional.',
  },
  {
    author_name: 'Gabriela K Perez',
    rating: 5,
    relative_time_description: 'a year ago',
    text: 'Elvia and her team are so great! I had a very large job that needed attention and they handled it perfectly.',
  },
];

const ReviewCard = ({ review }) => {
  const initials = review.author_name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="bg-white rounded-xl p-6 border border-[#E2E8F0] shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4 mb-4">
        {/* Avatar */}
        <div className="w-12 h-12 rounded-full bg-[#E3F2FD] flex items-center justify-center flex-shrink-0">
          <span className="text-[#1E88E5] font-semibold text-sm">{initials}</span>
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-[#0F172A] truncate" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {review.author_name}
          </h4>
          <p className="text-xs text-[#64748B]">{review.relative_time_description}</p>
        </div>
        {/* Google Logo */}
        <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
      </div>
      
      {/* Stars */}
      <div className="flex gap-0.5 mb-3">
        {[...Array(5)].map((_, i) => (
          <Star 
            key={i} 
            size={16} 
            className={i < review.rating ? 'text-[#FFD700] fill-[#FFD700]' : 'text-[#E2E8F0]'} 
          />
        ))}
      </div>
      
      {/* Review Text */}
      <p className="text-[#334155] text-sm leading-relaxed">
        "{review.text}"
      </p>
    </div>
  );
};

export const GoogleReviews = () => {
  const { t } = useLanguage();

  return (
    <section className="py-16 md:py-24 bg-white" data-testid="google-reviews-section">
      <div className="container mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16 animate-fadeInUp">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {t('reviews.title')} <span className="text-[#4CAF50]">{t('reviews.titleHighlight')}</span>
          </h2>
          
          {/* Rating Summary */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4">
            <div className="flex items-center gap-2">
              <span className="text-4xl font-bold text-[#0F172A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                4.9
              </span>
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={24} className="text-[#FFD700] fill-[#FFD700]" />
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 text-[#334155]">
              <span className="font-medium">{t('reviews.rating')}</span>
              <span>•</span>
              <span>{t('reviews.reviewCount')}</span>
            </div>
          </div>
          
          {/* Google Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F8FAFC] rounded-full border border-[#E2E8F0]">
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="text-sm font-medium text-[#334155]">{t('reviews.verifiedReviews')}</span>
          </div>
        </div>

        {/* Reviews Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {googleReviews.map((review, index) => (
            <div 
              key={index}
              className="animate-fadeInUp"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <ReviewCard review={review} />
            </div>
          ))}
        </div>
        
        {/* Powered by Google Note */}
        <p className="text-center text-sm text-[#64748B] mt-8">
          {t('reviews.poweredBy')}
        </p>
      </div>
    </section>
  );
};

export default GoogleReviews;
